import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
} from '@tanstack/react-table';
import {
  Table,
  Form,
  Button,
  Container,
  Row,
  Col,
  Modal,
} from 'react-bootstrap';
import { trackJobManagement } from '../utils/analytics';


const BacklogTable = ({ data = [], isLoading = false, onDelete }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState([]);
  
  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Define columns
  const columnHelper = createColumnHelper();
  
  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'User',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('gsc_property', {
        header: 'Property',
        cell: (info) => (
          <span className="text-break">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('domain', {
        header: 'Domain',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: (info) => (
          <span Style={info.getValue() === 'add' ? 'color:#1abc9c;' : 'color:#e74c3c;'}>
            {info.getValue() === 'add' ? 'Add' : 'Remove'}
          </span>
        ),
      }),
      columnHelper.accessor('permissions', {
        header: 'Permissions',
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          let variant = 'secondary';
          let text = status;
          let txcolor ='';
          let bgcolor = '';

          
          switch (status) {
            case 'valid':
              variant = 'success';
              text = '✓ Valid';
              txcolor = "var(--light);";
              bgcolor = '#1abc9c!important;';
              break;
            case 'pending_validation':
              variant = 'warning';
              text = '⏳ Validating';
              txcolor = 'var(--dark);';
              bgcolor = '#ffba00!important;';
              break;
            case 'not_google':
              variant = 'danger';
              text = '✗ Not Google';
              txcolor = 'var(--light);';
              bgcolor = '#e74c3c!important;';
              break;
            case 'invalid_email':
              variant = 'danger';
              text = '✗ Invalid Email';
              txcolor = 'var(--light);';
              bgcolor = '#e74c3c!important;';
              break;
            default:
              break;
          }
          
          return (
            <span className={`badge bg-${variant}`} Style={`color:${txcolor}background-color:${bgcolor};`}>
              {text}
            </span>
          );
        },
      }),
      columnHelper.accessor('requestor_email', {
        header: 'Requested By',
        cell: (info) => info.getValue() || 'Unknown',
      }),
      columnHelper.accessor('created_at', {
        header: 'Added',
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return '-';
          // Extract date and time parts from ISO string
          const [date, time] = timestamp.split('T');
          const timeWithoutMs = time.split('.')[0]; // Remove milliseconds
          return `${date}, ${timeWithoutMs}`; // ← ADD THIS LINE
          // older version
          // const date = new Date(timestamp);
          // return date.toLocaleString();
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Remove',
        cell: (props) => (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setItemToDelete(props.row.original);
              setShowConfirm(true);
            }}
            title="Remove from queue"
            Style="background-color:#e74c3c!important;"
          >
            <i className="fas fa-times"></i>
          </Button>
        ),
      }),
    ],
    []
  );
  
  // Create table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  
  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (itemToDelete) {
      onDelete(itemToDelete.org, itemToDelete.email_property);
      setShowConfirm(false);
      setItemToDelete(null);

      //trackjob removal in analytics
      //const jobsRemoved = response.jobs_removed || jobIds.length; //refine when bulk gets added, will need to return the api response as well
      const jobsRemoved = 1
      trackJobManagement('job_removed', jobsRemoved);
    }
  };
  
  if (data.length === 0) {
    return (
      <div className="text-center p-5 bg-light rounded">
        <p className="text-muted mb-0">No pending actions in backlog</p>
      </div>
    );
  }
  
  return (
    <Container fluid className="px-0">
      {/* Pagination controls - Top */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <span className="me-2 text-muted">Show &nbsp;</span>
            <Form.Select
              value={pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              style={{ width: 'auto'}}
            >
              {[10, 25, 50, 100, 250, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
            <span className="ms-2 text-muted">&nbsp; entries</span>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
      
      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th 
                    key={header.id}
                    style={{ 
                      width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span style={{ fontSize: '0.8em' }}>
                          {header.column.getIsSorted() === 'asc' ? '▲' : 
                           header.column.getIsSorted() === 'desc' ? '▼' : 
                           '⇅'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      
      {/* Pagination controls - Bottom */}
      <Row className="mt-3 align-items-center">
        <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
          <span className="text-muted">
            Showing {table.getRowModel().rows.length} of {data.length} entries
          </span>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-md-end">
            <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
            </span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="me-1"
            >
              Previous
            </Button>&nbsp;
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </Col>
      </Row>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove from Backlog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemToDelete && (
            <>
              Are you sure you want to remove this action from the queue?
              <br /><br />
              <strong>User:</strong> {itemToDelete.email}<br />
              <strong>Property:</strong> {itemToDelete.gsc_property}<br />
              <strong>Action:</strong> {itemToDelete.action}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BacklogTable;






// import React, { useState, useMemo } from 'react';
// import {
//   useReactTable,
//   getCoreRowModel,
//   flexRender,
//   getPaginationRowModel,
//   getSortedRowModel,
//   createColumnHelper,
// } from '@tanstack/react-table';
// import {
//   Table,
//   Form,
//   Button,
//   Container,
//   Row,
//   Col,
//   Spinner,
//   Modal,
// } from 'react-bootstrap';

// const BacklogTable = ({ data = [], isLoading = false, onDelete }) => {
//   const [pagination, setPagination] = useState({
//     pageIndex: 0,
//     pageSize: 10,
//   });
//   const [sorting, setSorting] = useState([]);
  
//   // Confirmation modal state
//   const [showConfirm, setShowConfirm] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
  
//   // Define columns
//   const columnHelper = createColumnHelper();
  
//   const columns = useMemo(
//     () => [
//       columnHelper.accessor('email', {
//         header: 'User',
//         cell: (info) => info.getValue(),
//       }),
//       columnHelper.accessor('gsc_property', {
//         header: 'Property',
//         cell: (info) => (
//           <span className="text-break">{info.getValue()}</span>
//         ),
//       }),
//       columnHelper.accessor('domain', {
//         header: 'Domain',
//         cell: (info) => info.getValue() || '-',
//       }),
//       columnHelper.accessor('action', {
//         header: 'Action',
//         cell: (info) => (
//           <span Style={info.getValue() === 'add' ? 'color:#1abc9c;' : 'color:#e74c3c;'}>
//             {info.getValue() === 'add' ? 'Add' : 'Remove'}
//           </span>
//         ),
//       }),
//       columnHelper.accessor('permissions', {
//         header: 'Permissions',
//         cell: (info) => info.getValue() || '-',
//       }),
//       columnHelper.accessor('status', {
//         header: 'Status',
//         cell: (info) => {
//           const status = info.getValue();
//           let variant = 'secondary';
//           let text = status;
//           let txcolor ='';
//           let bgcolor = '';

          
//           switch (status) {
//             case 'valid':
//               variant = 'success';
//               text = '✓ Valid';
//               txcolor = "var(--light);";
//               bgcolor = '#1abc9c!important;';
//               break;
//             case 'pending_validation':
//               variant = 'warning';
//               text = '⏳ Validating';
//               txcolor = 'var(--dark);';
//               bgcolor = '#ffba00!important;';
//               break;
//             case 'not_google':
//               variant = 'danger';
//               text = '✗ Not Google';
//               txcolor = 'var(--light);';
//               bgcolor = '#e74c3c!important;';
//               break;
//             case 'invalid_email':
//               variant = 'danger';
//               text = '✗ Invalid Email';
//               txcolor = 'var(--light);';
//               bgcolor = '#e74c3c!important;';
//               break;
//             default:
//               break;
//           }
          
//           return (
//             <span className={`badge bg-${variant}`} Style={`color:${txcolor}background-color:${bgcolor};`}>
//               {text}
//             </span>
//           );
//         },
//       }),
//       columnHelper.accessor('requestor_email', {
//         header: 'Requested By',
//         cell: (info) => info.getValue() || 'Unknown',
//       }),
//       columnHelper.accessor('created_at', {
//         header: 'Added',
//         cell: (info) => {
//           const timestamp = info.getValue();
//           if (!timestamp) return '-';
//           const date = new Date(timestamp * 1000);
//           return date.toLocaleString();
//         },
//       }),
//       columnHelper.display({
//         id: 'actions',
//         header: 'Remove',
//         cell: (props) => (
//           <Button
//             variant="danger"
//             size="sm"
//             onClick={() => {
//               setItemToDelete(props.row.original);
//               setShowConfirm(true);
//             }}
//             title="Remove from queue"
//             Style="background-color:#e74c3c!important;"
//           >
//             <i className="fas fa-times"></i>
//           </Button>
//         ),
//       }),
//     ],
//     []
//   );
  
//   // Create table instance
//   const table = useReactTable({
//     data,
//     columns,
//     state: {
//       pagination,
//       sorting,
//     },
//     onPaginationChange: setPagination,
//     onSortingChange: setSorting,
//     getCoreRowModel: getCoreRowModel(),
//     getPaginationRowModel: getPaginationRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//   });
  
//   // Handle delete confirmation
//   const handleConfirmDelete = () => {
//     if (itemToDelete) {
//       onDelete(itemToDelete.org, itemToDelete.email_property);
//       setShowConfirm(false);
//       setItemToDelete(null);
//     }
//   };
  
//   if (isLoading) {
//     return (
//       <div className="text-center p-5">
//         <Spinner animation="border" />
//         <p className="mt-2">Loading backlog...</p>
//       </div>
//     );
//   }
  
//   if (data.length === 0) {
//     return (
//       <div className="text-center p-5 bg-light rounded">
//         <p className="text-muted mb-0">No pending actions in backlog</p>
//       </div>
//     );
//   }
  
//   return (
//     <Container fluid className="px-0">
//       {/* Pagination controls - Top */}
//       <Row className="mb-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0">
//           <div className="d-flex align-items-center">
//             <span className="me-2 text-muted">Show &nbsp;</span>
//             <Form.Select
//               value={pagination.pageSize}
//               onChange={(e) => {
//                 table.setPageSize(Number(e.target.value));
//               }}
//               style={{ width: 'auto'}}
//             >
//               {[10, 25, 50, 100, 250, 500].map((pageSize) => (
//                 <option key={pageSize} value={pageSize}>
//                   {pageSize}
//                 </option>
//               ))}
//             </Form.Select>
//             <span className="ms-2 text-muted">&nbsp; entries</span>
//           </div>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
      
//       {/* Table */}
//       <div className="table-responsive">
//         <Table striped bordered hover>
//           <thead style={{ backgroundColor: '#343a40', color: 'white' }}>
//             {table.getHeaderGroups().map((headerGroup) => (
//               <tr key={headerGroup.id}>
//                 {headerGroup.headers.map((header) => (
//                   <th 
//                     key={header.id}
//                     style={{ 
//                       width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
//                       textAlign: 'center',
//                       verticalAlign: 'middle',
//                       cursor: header.column.getCanSort() ? 'pointer' : 'default',
//                       userSelect: 'none',
//                     }}
//                     onClick={header.column.getToggleSortingHandler()}
//                   >
//                     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
//                       {header.isPlaceholder
//                         ? null
//                         : flexRender(header.column.columnDef.header, header.getContext())}
//                       {header.column.getCanSort() && (
//                         <span style={{ fontSize: '0.8em' }}>
//                           {header.column.getIsSorted() === 'asc' ? '▲' : 
//                            header.column.getIsSorted() === 'desc' ? '▼' : 
//                            '⇅'}
//                         </span>
//                       )}
//                     </div>
//                   </th>
//                 ))}
//               </tr>
//             ))}
//           </thead>
//           <tbody>
//             {table.getRowModel().rows.map((row) => (
//               <tr key={row.id}>
//                 {row.getVisibleCells().map((cell) => (
//                   <td key={cell.id}>
//                     {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </Table>
//       </div>
      
//       {/* Pagination controls - Bottom */}
//       <Row className="mt-3 align-items-center">
//         <Col xs={12} md={6} className="mb-2 mb-md-0" Style="text-align: justify;">
//           <span className="text-muted">
//             Showing {table.getRowModel().rows.length} of {data.length} entries
//           </span>
//         </Col>
//         <Col xs={12} md={6}>
//           <div className="d-flex justify-content-md-end">
//             <span className="me-3 align-self-center text-muted" Style="color:var(--dark);">
//               Page {pagination.pageIndex + 1} of {table.getPageCount()}&nbsp;&nbsp;
//             </span>
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.previousPage()}
//               disabled={!table.getCanPreviousPage()}
//               className="me-1"
//             >
//               Previous
//             </Button>&nbsp;
//             <Button
//               variant="outline-primary"
//               size="sm"
//               onClick={() => table.nextPage()}
//               disabled={!table.getCanNextPage()}
//             >
//               Next
//             </Button>
//           </div>
//         </Col>
//       </Row>
      
//       {/* Confirmation Modal */}
//       <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Remove from Backlog</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           {itemToDelete && (
//             <>
//               Are you sure you want to remove this action from the queue?
//               <br /><br />
//               <strong>User:</strong> {itemToDelete.email}<br />
//               <strong>Property:</strong> {itemToDelete.gsc_property}<br />
//               <strong>Action:</strong> {itemToDelete.action}
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowConfirm(false)}>
//             Cancel
//           </Button>
//           <Button variant="danger" onClick={handleConfirmDelete}>
//             Remove
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </Container>
//   );
// };

// export default BacklogTable;